import { createFileRoute, type ErrorComponentProps } from '@tanstack/react-router'

import ScreenLayout from '@/components/layout/ScreenLayout'
import CountUp from '@/components/ui/CountUp'
import DataTable, { type DataTableColumn } from '@/components/ui/DataTable'
import EmptyState from '@/components/ui/EmptyState'
import Money from '@/components/ui/Money'
import { directionTone } from '@/components/ui/market-tone'
import TitleRow from '@/components/ui/TitleRow'
import { useIndexSymbols, useOpenTrades } from '@/features/signals/api/queries'
import { formatDate, formatPercent } from '@/lib/format'
import { money, sign, type Money as MoneyValue } from '@/lib/money'
import type { OpenTrade } from '@/schemas/signals'
import MissingFigure from '@/screens/signals/components/MissingFigure'
import { signalCompanyColumn } from '@/screens/signals/components/SignalCompanyColumn'
import PastSessionEmpty from '@/screens/signals/components/PastSessionEmpty'
import SessionDatePicker from '@/screens/signals/components/SessionDatePicker'
import SignalsEmpty from '@/screens/signals/components/SignalsEmpty'
import SignalsFootnote from '@/screens/signals/components/SignalsFootnote'
import SignalsRouteError from '@/screens/signals/components/SignalsRouteError'
import SignalsSearchBox from '@/screens/signals/components/SignalsSearchBox'
import { PickerSkeleton } from '@/screens/signals/components/SignalsSkeletons'
import SignalsTableFrame from '@/screens/signals/components/SignalsTableFrame'
import SignalsToolbar from '@/screens/signals/components/SignalsToolbar'
import {
  FootnoteSkeleton,
  SearchSkeleton,
  TableFrameSkeleton,
  ToolbarSkeleton,
} from '@/screens/signals/components/SignalsTableSkeletons'
import { signalsRailProps } from '@/screens/signals/components/signals-rail'
import { useSearchBox } from '@/screens/signals/lib/search-box'
import { SIGNAL_ROWS_52 } from '@/screens/signals/lib/signal-rows'
import { useIsPastSession } from '@/screens/signals/lib/session-status'
import { signalRowLink } from '@/screens/signals/lib/signals-ui'
import { loadOpenTrades, openTradesSearchSchema } from '@/screens/signals/OpenTradesScreen.loader'
import { SIGNAL_DEFAULT_PAGE_SIZE, toSignalPageSize } from '@/screens/signals/search'

const TITLE = 'Open Trades'

/** The strip names the page, so the header is the bare title (`sr-only` from `lg`). */
const HEADER = <TitleRow title={TITLE} />

/** The search box at the left and the static date at the right, in bars. */
const PENDING_TOOLBAR = <ToolbarSkeleton start={<SearchSkeleton />} end={<PickerSkeleton />} />

/*
 * The loaded page, block for block: the search and date row, the table panel
 * on two-line rows (fifteen, the opening page size), its pager and the
 * price-basis note. Dev has about a hundred open trades, so the pager is
 * always there. No session picker: the read is current only (D3). No subtitle
 * (owner 2026-10-12).
 */
function OpenTradesPending() {
  return (
    <ScreenLayout
      pending
      pendingLabel="Loading open trades…"
      header={HEADER}
      {...signalsRailProps(TITLE)}
    >
      <TableFrameSkeleton
        rows={SIGNAL_DEFAULT_PAGE_SIZE}
        columns={4}
        toolbar={PENDING_TOOLBAR}
        tableClassName={SIGNAL_ROWS_52}
      />
      <FootnoteSkeleton className="h-8 @xl:h-4" />
    </ScreenLayout>
  )
}

function OpenTradesError(props: ErrorComponentProps) {
  return (
    <SignalsRouteError
      {...props}
      header={HEADER}
      pageTitle={TITLE}
      title="Open trades did not load"
    />
  )
}

export const Route = createFileRoute('/_app/market/signals/open-trades')({
  validateSearch: openTradesSearchSchema,
  loader: loadOpenTrades,
  pendingComponent: OpenTradesPending,
  errorComponent: OpenTradesError,
  component: OpenTradesScreen,
})

/** One frame of Since's count-up, at the API's 2 dp. */
const formatSince = (value: number): string => formatPercent(money(value.toFixed(2)))

/**
 * "Since", 14/400 in the market colours and counting up from 0 on mount (A4, 700ms; reduced motion
 * shows the final figure). The colour and the screen reader's direction word come from the final
 * value, so the count never flips through the other colour. Like `Change`, without its sign
 * arithmetic on a frame.
 */
function SinceFigure({ value }: { value: MoneyValue }) {
  const direction = sign(value)
  return (
    <span className={`tnum ${directionTone(direction)}`} data-numeric>
      <span className="sr-only">
        {direction > 0 ? 'up' : direction < 0 ? 'down' : 'unchanged'}{' '}
      </span>
      <CountUp value={Number(value)} format={formatSince} />
    </span>
  )
}

/**
 * The API's order, biggest move first (D6): no column sorts. "Price then" is the signal day's LOW
 * (D1), which the note under the table says in words. Open trades are greens, so a low it always
 * is. The mock's columns: 14/400, Signal day 120 and Price then 110, Since 96 in the market
 * colours, hugging the right edge, the Company column taking the rest.
 */
const COLUMNS: readonly DataTableColumn<OpenTrade>[] = [
  signalCompanyColumn<OpenTrade>({ fill: true }),
  {
    id: 'signalDay',
    accessorFn: (row) => row.signalDate,
    header: 'Signal day',
    enableSorting: false,
    meta: { numeric: true, secondary: true, width: '132px' },
    cell: ({ row }) => formatDate(row.original.signalDate),
  },
  {
    id: 'priceThen',
    accessorFn: (row) => row.signalPrice ?? '',
    header: 'Price then',
    enableSorting: false,
    meta: { numeric: true, hideBelow: 'sm', width: '122px' },
    cell: ({ row }) =>
      row.original.signalPrice === null ? (
        <MissingFigure />
      ) : (
        <Money value={row.original.signalPrice} />
      ),
  },
  {
    id: 'since',
    accessorFn: (row) => row.sincePercent ?? '',
    header: 'Since',
    enableSorting: false,
    meta: { numeric: true, width: '96px' },
    cell: ({ row }) =>
      row.original.sincePercent === null ? (
        <MissingFigure />
      ) : (
        <SinceFigure value={row.original.sincePercent} />
      ),
  },
]

/**
 * Signal Zone · Open trades — every green signal no red one has closed, with
 * its move since the signal day, biggest first.
 *
 * MAIN read keyed on nothing, so suspense: the loader always has it. Dev
 * sends every row in one answer (about a hundred), so paging is the client's,
 * over `?page=` and `?pageSize=`; a page past the end shows the last. The
 * search box is the client's too: the read has no `q`, so it filters the rows
 * in hand by symbol prefix (the box Trend and History have, on the same `?q=`).
 * `[]` is "no open trades", an answer. The D1 price-basis note sits under the
 * table, and never says "from the close". The date at the toolbar's right is the session
 * picker; the read is current only, so a past pick shows `PastSessionEmpty`
 * (`asks/signals-dates.md`).
 */
function OpenTradesScreen() {
  const search = Route.useSearch()
  const navigate = Route.useNavigate()
  const { rows: all } = useOpenTrades().data
  const indexSymbols = useIndexSymbols()
  const [term, setTerm] = useSearchBox(search.q, (q) => {
    void navigate({ search: (prev) => ({ ...prev, q, page: 1 }), replace: true })
  })

  const past = useIsPastSession(search.date)
  const picker = (
    <SessionDatePicker
      value={search.date ?? null}
      onChange={(date) => {
        void navigate({ search: (prev) => ({ ...prev, date, page: 1 }) })
      }}
    />
  )

  if (past && search.date !== undefined) {
    // The read serves today's trades only: a past day keeps the picker and answers with no data.
    return (
      <ScreenLayout header={HEADER} {...signalsRailProps(TITLE)}>
        <div>
          <SignalsToolbar end={picker} />
          <PastSessionEmpty date={search.date} />
        </div>
      </ScreenLayout>
    )
  }

  if (all.length === 0) {
    return (
      <ScreenLayout header={HEADER} {...signalsRailProps(TITLE)}>
        <SignalsEmpty
          reason="list"
          title="No open trades"
          description="Every green signal has been closed by a red one."
        />
      </ScreenLayout>
    )
  }

  const prefix = search.q?.toUpperCase()
  const rows =
    prefix === undefined
      ? all
      : all.filter((row) => row.stock.symbol.toUpperCase().startsWith(prefix))
  const pageCount = Math.max(1, Math.ceil(rows.length / search.pageSize))
  const page = Math.min(search.page, pageCount)
  const shown = rows.slice((page - 1) * search.pageSize, page * search.pageSize)

  return (
    <ScreenLayout header={HEADER} {...signalsRailProps(TITLE)}>
      <SignalsTableFrame
        toolbar={<SignalsSearchBox value={term} onChange={setTerm} />}
        toolbarEnd={picker}
        paging={{
          page,
          pageSize: search.pageSize,
          total: rows.length,
          unit: 'trades',
          onPageChange: (next) => {
            void navigate({ search: (prev) => ({ ...prev, page: next }) })
          },
          onPageSizeChange: (pageSize) => {
            void navigate({
              search: (prev) => ({
                ...prev,
                pageSize: toSignalPageSize(pageSize, search.pageSize),
                page: 1,
              }),
            })
          },
        }}
      >
        {rows.length === 0 ? (
          <EmptyState kind="no-results" size="section" query={search.q ?? ''} />
        ) : (
          <DataTable
            columns={COLUMNS}
            rows={shown}
            getRowId={(row) => `${row.stock.symbol}-${row.signalDate}`}
            rowLinkOptions={signalRowLink(indexSymbols)}
            rowHeight="lg"
            bodySize="m"
            headWeight="regular"
            washTone="wash"
            nameUnderline={false}
            className={SIGNAL_ROWS_52}
            caption="Green signals that no red signal has closed yet, with the move since the signal day"
          />
        )}
      </SignalsTableFrame>
      {rows.length === 0 ? null : <SignalsFootnote basis="open-trades" />}
    </ScreenLayout>
  )
}
